import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';
import Button from '../../components/Button/Button';
import FormControl from '../../components/Forms/FormControl/FormControl';
import FormGroup from '../../components/Forms/FormGroup/FormGroup';

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PasswordRequirements from '../../components/PasswordRequirements/PasswordRequirements';
import { useUnprotectedLayout } from '../../components/UnprotectedRouteLayout/UnprotectedRouteLayout';
import errorMessages from '../../config/errorMessages';
import { Subtitle, Title } from './style';
import { confirmResetPassword } from 'aws-amplify/auth';

type Inputs = {
  password: string;
  confirmNewPassword: string;
};

const schema = yup.object().shape({
  password: yup
    .string()
    .required(errorMessages.required)
    .min(8, errorMessages.minCharacters(8))
    .matches(/[a-z]/g, 'Password must have lowercase characters')
    .matches(/[!@#$%^&*(),.?":{}|<>]/g, 'Password must have symbol characters')
    .matches(/[A-Z]/g, 'Password must have uppercase characters')
    .matches(/[0-9]/g, 'Password must have numeric characters'),
  confirmNewPassword: yup
    .string()
    .oneOf([yup.ref('password')], "Passwords don't match")
    .required(errorMessages.required),
});

const CreateNewPassword: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const code = searchParams.get('reset_code');
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showRequirements, setShowRequirements] = useState(false);

  const { setBackgroundImagePath } = useUnprotectedLayout();
  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
  } = useForm<Inputs>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    setBackgroundImagePath!('/images/earth.jpg');

    if (!code || !email) {
      navigate('/auth/forgot');
    }
  }, []);

  const onSubmit: SubmitHandler<Inputs> = async ({ password }) => {
    setIsLoading(true);
    setSubmitError(null);
    try {
      await confirmResetPassword({
        username: email!,
        confirmationCode: code!,
        newPassword: password,
      });
      setSuccess(true);
    } catch (e) {
      setSubmitError('Invalid or expired reset link. Please request a new one.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {success ? (
        <>
          <Title>Password Changed Successfully</Title>
          <Subtitle>It is now possible to login using your new credentials.</Subtitle>

          <FormGroup>
            <Button
              block
              onClick={() => { window.location.href = `${process.env.REACT_APP_EA_APP_URL}/auth/login`; }}
            >
              Log in
            </Button>
          </FormGroup>
        </>
      ) : (
        <>
          <Title>Create New Password</Title>

          <form onSubmit={handleSubmit(onSubmit)}>
            <FormGroup>
              <FormControl
                label="New Password"
                id="newPassword"
                type="password"
                name="password"
                register={register}
                error={errors.password?.message}
                onFocus={() => setShowRequirements(true)}
                onBlur={() => setShowRequirements(false)}
                dropdown={
                  showRequirements ? <PasswordRequirements password={watch('password')} /> : null
                }
              />
            </FormGroup>
            <FormGroup>
              <FormControl
                label="Confirm New Password"
                id="confirmNewPassword"
                type="password"
                name="confirmNewPassword"
                register={register}
                error={errors.confirmNewPassword?.message}
              />
            </FormGroup>
            {submitError && (
              <FormGroup>
                <span style={{ color: 'red', fontSize: 14 }}>{submitError}</span>
              </FormGroup>
            )}
            <FormGroup>
              <Button
                loading={isLoading}
                type="submit"
              >
                Reset Password
              </Button>
            </FormGroup>
          </form>
        </>
      )}
    </>
  );
};

export default CreateNewPassword;
